// src/belediye/pages/gorevler/GorevlerimPage.js
// FAZ-56 — "Görevlerim" (/yonetim/gorevlerim): İŞİ YAPANIN sayfası.
//
// Kapsam SAYFADAN gelir (K-2): kullanıcı burada YALNIZ kendine atanan görevleri
// görür. Tek istisna, çip şeridinin sonundaki "Birim Havuzu" — o bir KAPSAM
// çipidir (K-6): birime atanmış, henüz kimsenin üstlenmediği görevler. İzin
// (tasks.view_unit_pool) yoksa çip hiç çıkmaz.
//
// 2026-07-28 kullanıcı kararları:
//   • "İşe Başladım" küçük ÇİP; tıklayınca yanında POPOVER açılır, ne olacağını
//     anlatır ve tek "Onayla" ile gönderir (alttan pencere YOK).
//   • Statü çipleri kendi renkleriyle dolar; Birim Havuzu nötr gri kalır.

import React, { useState } from "react";
import {
  ChatBubbleOvalLeftEllipsisIcon,
  HandRaisedIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import usePermissions from "../../hooks/usePermissions";
import GorevListeIskeleti from "../../components/tasks/GorevListeIskeleti";
import { OrtakFiltreler } from "../../components/tasks/GorevFiltreleri";
import { OnayPopover, SoruPopover } from "../../components/tasks/GorevOnayPopover";
import { ASSIGNEE_PRESETS } from "../../components/tasks/GorevBits";
import { addComment, taskAction } from "../../services/internalTasksApi";
import "../../components/tasks/gorev.css";

const BILGI = (
  <div className="gk-bilgi">
    <p className="gk-bilgi-giris">
      Burası <b>size verilen</b> işlerin sayfası. Üzerinizde ne var, hangisi
      yaklaşıyor, hangisi gecikti — hepsi burada.
    </p>

    <h4>Üstteki başlıklar ne anlatıyor?</h4>
    <p className="gk-bilgi-ipucu">
      Her iş <b>tek bir başlıkta</b> durur; başlıklar birbirini tekrar etmez.
    </p>
    <dl className="gk-bilgi-liste">
      <dt data-t="yeni">Yeni</dt>
      <dd>
        Yolunda giden işleriniz: yeni gelenler, başladıklarınız, teslim tarihi
        uzak ya da hiç tarih verilmemiş olanlar.
      </dd>
      <dt data-t="onay">Onay Bekleyen</dt>
      <dd>İşi bitirip teslim ettiniz; <b>sıra talebi verende</b>. Onaylanınca kapanır.</dd>
      <dt data-t="geciken">Geciken</dt>
      <dd>Teslim tarihi geçmiş işler. Yetişmeyecekse ayrıntısından ek süre isteyebilirsiniz.</dd>
      <dt data-t="yaklasan">Yakındakiler</dt>
      <dd>Teslim tarihi kapıya dayanmış işler — önce bunlara bakın.</dd>
      <dt data-t="tamamlanan">Tamamlanan</dt>
      <dd>Kapanmış işleriniz — onaydan geçenler <i>ve</i> iptal edilenler.</dd>
      <dt data-t="havuz">Birim Havuzu</dt>
      <dd>
        Size değil <b>biriminize</b> verilmiş, henüz kimsenin üstlenmediği işler.
        Üstlendiğinizde iş sizin üzerinize geçer ve bu listeden düşer.
      </dd>
    </dl>

    <h4>Sağdaki kutular</h4>
    <p>
      Hangi başlıkta olursanız olun, sağda <b>üç kutu</b> durur ve o an
      bakmadığınız işleri gösterir. Bulunduğunuz başlığın kutusu tekrar
      gösterilmez — zaten solda tam listesi açıktır. Bir kutunun altındaki{" "}
      <b>Tümünü gör</b>, sizi o başlığa götürür.
    </p>

    <h4>Neler yapabilirsiniz?</h4>
    <ul>
      <li><b>İşe Başladım</b> — satırdan tek tıkla; talebi veren anında haberdar olur.</li>
      <li><b>Arama</b> — işin konusuyla ya da talebi veren kişinin adıyla arayın.</li>
      <li><b>Filtre</b> — teslim tarihi, ne zaman verildiği, aciliyet ve işin kaynağı.</li>
      <li><b>Bir işe tıklayın</b> — ayrıntısına girer, yazışır, bitirince onaya gönderirsiniz.</li>
    </ul>

    <p className="gk-bilgi-dip">
      Sizin <i>verdiğiniz</i> işler burada değil — yetkiniz varsa <b>Talep Gönder</b> sayfasında.
    </p>
  </div>
);

// "İşe başlayınca ne olur?" — sohbetteki teşvik balonuyla AYNI metin
// (ChatThread msj-startnudge-pop). İki yerde farklı şey yazmasın.
const BASLA_BILGISI = (
  <ul>
    <li>Talep <b>“Devam ediyor”</b> durumuna geçer.</li>
    <li>Sohbete “işe başlandı” bilgisi düşer, talebi veren anında görür.</li>
    <li>Süre sayacı işlemeye başlar.</li>
    <li>Durum bildirme, ek süre isteme ve onaya gönderme açılır.</li>
  </ul>
);

// NOT: "ben kimim / bu görev benim mi" hesabı burada YOK. Satır düğmelerinin
// koşulları backend'in `viewer` bloğundan gelir (TaskListSerializer) — kural
// tek yerde durur, iki taraf sapamaz.

export default function GorevlerimPage() {
  const { can } = usePermissions();
  const [islemde, setIslemde] = useState(0);

  // İzin YOKSA kapsam da yok: yalnız havuz izni olan kullanıcıda statü çipleri
  // hiç çıkmaz ve sayfa doğrudan havuza açılır (izinsiz scope backend'de 403).
  const anaKapsam = can("tasks.view_assigned") ? "assigned" : null;
  const havuzKapsami = can("tasks.view_unit_pool") ? "unit_pool" : null;

  const filtreler = ({ filtreler: f, yamala, gorunum }) => (
    <OrtakFiltreler filtreler={f} yamala={yamala} cip={gorunum === "tamamlanan" ? "tamamlanan" : ""} />
  );

  // FAZ-58 — sağdaki dikkat şeridi. Görev Ata ile AYNI mantık, yapanın gözüyle:
  // gecikenler → onay bekleyenler → yakındakiler. Aktif sekmenin kartı yerini
  // "yeni gelenler"e bırakır. Birim Havuzu artık şerit kartı değil SEKME
  // (kendi kapsamı var, listenin tamamı gerekiyor).
  const seritKartlari = ({ scope }) => {
    if (!scope) return null;
    return {
      gecikti: {
        key: "gecikti",
        baslik: "Süresi geçenler",
        tone: "gecikti",
        sekme: "geciken",
        bosMetin: "Geciken işiniz yok.",
        params: { scope, overdue: 1, sort: "termin" },
      },
      onayda: {
        key: "onayda",
        baslik: "Onayda bekleyenler",
        tone: "onayda",
        sekme: "onay",
        bosMetin: "Onayda bekleyen teslimatınız yok.",
        params: { scope, status: "onay_bekliyor", sort: "bekleyen" },
      },
      yakinda: {
        key: "yakinda",
        baslik: "Yakındakiler",
        tone: "yakinda",
        sekme: "yakinda",
        bosMetin: "Süresi yaklaşan işiniz yok.",
        params: { scope, due_soon: 1, sort: "termin" },
      },
      yeni: {
        key: "yeni",
        baslik: "Son gelenler",
        tone: "yeni",
        sekme: "yeni",
        sort: "yeni",
        bosMetin: "Bekleyen yeni iş yok.",
        // "Yeni" sekmesinin kümesi, en son geleni üstte.
        params: { scope, kume: "yeni", sort: "yeni" },
      },
    };
  };

  const uygula = async (task, action, okMsg, tazele) => {
    setIslemde(task.id);
    try {
      await taskAction(task.id, action);
      toast.success(okMsg);
      tazele();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem yapılamadı.");
    } finally {
      setIslemde(0);
    }
  };

  // Hazır durum cümlesi → göreve yorum. Atayanın "Ne durumdayız?" akışıyla
  // simetrik: tıklanan cümle DOĞRUDAN gider, ara form yok.
  const durumBildir = async (task, cumle, tazele) => {
    setIslemde(task.id);
    try {
      await addComment(task.id, cumle);
      toast.success("Durum bildirildi — talebi veren haberdar edildi.");
      tazele();
    } catch (e) {
      // Sunucunun kendi gerekçesi öncelikli: "Bu görev kapandı — yazışma
      // yapılamaz" ya da "Mesajlaşma modülü kapalı" gibi mesajlar kullanıcıya
      // genel bir hata metninden çok daha fazlasını söylüyor.
      toast.error(e?.response?.data?.detail || "Durum bildirilemedi.");
    } finally {
      setIslemde(0);
    }
  };

  const kartAksiyonlari = (task, { tazele, havuzda }) => {
    if (havuzda) {
      // Üstlenme koşullarının TAMAMI backend'de (permissions.can_accept):
      // izin + görev açık + birimde aktif üyelik + "kendi açtığın havuz
      // görevini üstlenemezsin" (self-atama yasağının arka kapısı). Satır bu
      // dörtlüyü kopyalamak yerine tek bayrağı okur.
      if (!task?.viewer?.can_accept) return null;
      return (
        <OnayPopover
          label="Talebi üstlen"
          baslik="Bu işi üstleniyor musunuz?"
          bilgi={
            <ul>
              <li>Talep havuzdan çıkar ve <b>sizin üzerinize</b> geçer.</li>
              <li>Talebi veren, işi üstlendiğinizi görür.</li>
              <li>Bundan sonra iş <b>Gelen Talepler</b> listenizde durur.</li>
            </ul>
          }
          onayLabel="Evet, üstleniyorum"
          onOnay={() => uygula(task, "accept", "Talebi üstlendiniz.", tazele)}
          trigger={
            <span
              className={`gk-satirchip gk-satirchip--birincil ${
                islemde === task.id ? "is-busy" : ""
              }`}
            >
              <HandRaisedIcon className="gk-ic" /> Üstlen
            </span>
          }
        />
      );
    }

    // ── İŞİ YAPANIN İKİ ADIMI (kullanıcı kararı 2026-07-28) ──────────────
    // Atayan tarafta satırda tek bir çip var: "Ne durumdayız?". Atanan tarafta
    // da AYNI yerde, AYNI popover'la tek çip olmalı — ama işin evresine göre
    // değişerek:
    //
    //   1) henüz başlamadıysan  → "İşe Başladım"  (onay popover'ı)
    //   2) başladıktan sonra    → "Durum bildir"  (hazır cevap popover'ı)
    //
    // İkinci adım eskiden YOKTU: kişi işe başlayınca satırdaki çip kayboluyor,
    // durum bildirmek için göreve girip sohbeti açmak gerekiyordu.
    if (task?.viewer?.can_start) {
      return (
        <OnayPopover
          label="İşe başla"
          baslik="İşe başlayınca ne olur?"
          bilgi={BASLA_BILGISI}
          onayLabel="İşe başlıyorum"
          onOnay={() =>
            uygula(task, "start", "Talebe başladınız — talebi verene haber gitti.", tazele)
          }
          trigger={
            <span
              className={`gk-satirchip gk-satirchip--olumlu ${
                islemde === task.id ? "is-busy" : ""
              }`}
            >
              <PlayIcon className="gk-ic" /> İşe Başladım
            </span>
          }
        />
      );
    }

    // "Durum bildir" — yalnız İŞİ YAPAN tarafta ve iş üzerindeyken. Atayanın
    // "Ne durumdayız?" çipiyle simetrik: hazır cümleye tıklamak doğrudan
    // gönderir, göreve yorum olarak düşer ve veren bildirim alır.
    if (task?.viewer?.is_doer && task?.viewer?.can_comment && task.status === "devam_ediyor") {
      return (
        <SoruPopover
          label="Durum bildir"
          baslik="Ne bildirmek istersiniz?"
          dip="Seçtiğiniz bilgi talebe düşer, talebi veren anında görür."
          sorular={ASSIGNEE_PRESETS}
          onSec={(cumle) => durumBildir(task, cumle, tazele)}
          trigger={
            <span
              className={`gk-satirchip gk-satirchip--sor ${
                islemde === task.id ? "is-busy" : ""
              }`}
            >
              <ChatBubbleOvalLeftEllipsisIcon className="gk-ic" /> Durum bildir
            </span>
          }
        />
      );
    }

    return null;
  };

  const bosMetin = (sekme) => {
    if (sekme === "havuz") return "Biriminizin havuzunda bekleyen iş yok.";
    if (sekme === "tamamlanan") return "Bu aralıkta kapanmış işiniz yok.";
    if (sekme === "onay") return "Onayda bekleyen teslimatınız yok.";
    if (sekme === "geciken") return "Geciken işiniz yok 🎉";
    if (sekme === "yakinda") return "Teslim tarihi yaklaşan işiniz yok.";
    return "Üzerinizde açık iş yok 🎉";
  };

  return (
    <GorevListeIskeleti
      title="Gelen Talepler"
      accent="bana"
      scope={anaKapsam}
      poolScope={havuzKapsami}
      infoTitle="Gelen Talepler"
      infoContent={BILGI}
      infoWide
      searchPlaceholder="İş konusu ya da talebi veren kişi…"
      renderFilters={filtreler}
      renderCardActions={kartAksiyonlari}
      railKartlari={seritKartlari}
      tarafGoster="veren"
      bosMetin={bosMetin}
    />
  );
}
